import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock, AlertTriangle } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const PortalPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="portal-page">
      <SEOHead 
        title="Certification Portal - OnPoint Authority Systems"
        description="Certification is a control mechanism. It exists to validate authority competence, establish enforcement readiness, and protect the integrity of the system."
        url="https://onpointauthority.com/portal"
      />
      <Navbar />

      {/* Licensed Access Banner */}
      <div className="bg-primary/10 border-b border-primary/20 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Lock className="h-4 w-4 text-primary" />
          <p className="text-xs text-primary font-medium tracking-wider uppercase">
            Licensed Access Only
          </p>
        </div>
      </div>

      {/* Hero - Certification Is A Gate */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-6 md:px-12" data-testid="portal-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Certification Is a Gate,<br />
              <span className="text-primary">Not a Credential</span>
            </h1>
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
              <p className="text-foreground">
                Certification within OnPoint Authority Systems is a control mechanism.
              </p>
              <p>It exists to:</p>
              <ul className="space-y-3">
                {[
                  "Validate authority competence",
                  "Establish enforcement readiness",
                  "Protect the integrity of the system",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="pt-4">
                Certification does not confer permanence.<br />
                <span className="text-foreground">It confers conditional access.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Certification Works */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="how-certification-works">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              How Certification Works
            </p>
            <ul className="space-y-4">
              {[
                "Modules must be completed in sequence",
                "Exams are timed and audited",
                "Failure results in delay or denial",
                "Certification may be revoked",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
            <p className="text-muted-foreground mt-8 italic">
              No exceptions are made for experience, tenure, or reputation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Certification Enables */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="certification-enables">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              What Certification Enables
            </p>
            <p className="text-lg text-muted-foreground mb-4">
              Certification allows an operator to be:
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Considered for licensing",
                "Granted portal access",
                "Subject to audit participation",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-foreground">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-foreground font-medium">
              Licensing decisions are separate and discretionary.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing Notice */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="portal-notice">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Authority is monitored continuously.<br />
                    Certification status may change without notice.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/admin" 
                className="text-sm text-primary hover:text-primary/80 transition-colors"
                data-testid="admin-login-link"
              >
                Administrator Login →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortalPage;
